import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DatePicker from '../components/common/DatePicker';
import { SolicitacoesService } from '../services/solicitacoesService';
import { ComprovanteService } from '../services/comprovanteService';
import StatusFlow from '../components/common/StatusFlow';
import { ValidationService } from '../services/validationService';
import { UploadService } from '../services/uploadService';
import { StatusSolicitacao } from '../types';
import { usePermissoes } from '../hooks/usePermissoes';
import { AvisosService } from '../services/avisosService';
import { ConfiguracoesService } from '../services/configuracoesService';
import MagneticButton from '../components/common/MagneticButton';
import AlertModal from '../components/common/AlertModal';

const Inicio: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const { temPermissao: podeSolicitarAgendamento, carregando: carregandoPermissao } = usePermissoes('inicio', 'solicitar_agendamento');
  const { temPermissao: podeFecharDia, carregando: carregandoFecharDia } = usePermissoes('cargas', 'fechar_dia');

  // Função para obter o nome da agenda baseado na filial selecionada
  const getNomeAgenda = () => {
    switch (filialAtual) {
      case 'FILIAL_1':
        return 'AGENDA UP';
      case 'FILIAL_2':
        return 'AGENDA ALEPLAST';
      case 'FILIAL_3':
        return 'AGENDA ALEPACK';
      default:
        return 'AGENDA UP';
    }
  };

  const [mesAtual, setMesAtual] = useState(new Date());
  const [diasFechados, setDiasFechados] = useState<any[]>([]);
  const [avisos, setAvisos] = useState<any[]>([]);
  const [termosRegras, setTermosRegras] = useState({
    titulo: 'Regras obrigatórias para solicitação de agendamento',
    texto: '',
    postosFiscais: ''
  });
  const [solicitacoes, setSolicitacoes] = useState<any[]>([]);
  const [loadingSolicitacoes, setLoadingSolicitacoes] = useState(false);
  const [buscaSolicitacoes, setBuscaSolicitacoes] = useState('');
  const [buscaData, setBuscaData] = useState('');
  const [horarioFuncionamento, setHorarioFuncionamento] = useState('8:00 às 14:00');
  const [horarioRecebimentoNF, setHorarioRecebimentoNF] = useState('8:00 às 17:00');
  const [showModalSolicitacao, setShowModalSolicitacao] = useState(false);
  const [showModalFecharDia, setShowModalFecharDia] = useState(false);
  const [alertModal, setAlertModal] = useState<{ open: boolean; type: 'warning' | 'error' | 'info' | 'success'; title: string; messages?: string[]; message?: string; confirmText?: string; autoClose?: number; onConfirm?: () => void }>({
    open: false,
    type: 'warning',
    title: '',
  });
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const [solicitacaoDocumentos, setSolicitacaoDocumentos] = useState<any>(null);
  const [diaSelecionado, setDiaSelecionado] = useState<string | undefined>(undefined);
  const [ocultarFinalizadas, setOcultarFinalizadas] = useState(false);
  const [termosAceitos, setTermosAceitos] = useState(false);
  const [loadingDias, setLoadingDias] = useState(false);
  const [enviandoSolicitacao, setEnviandoSolicitacao] = useState(false);
  const [formData, setFormData] = useState({
    fornecedor: user?.nome || '',
    fornecedor_mercadoria: '',
    peso: '',
    volume: '',
    tipoProduto: '',
    tipoCarga: '',
    dataDesejada: new Date().toISOString().split('T')[0],
    observacoes: '',
    documentos: [{ file: new File([], ''), fornecedorMercadoria: '' }] as Array<{file: File, fornecedorMercadoria: string}>
  });

  // Carregar dias fechados do Supabase
  useEffect(() => {
    const carregarDiasFechados = async () => {
      try {
        setLoadingDias(true);
        const dados = await SolicitacoesService.buscarDiasFechados(filialAtual);
        setDiasFechados(dados);
      } catch (error) {
        console.error('Erro ao carregar dias fechados:', error);
      } finally {
        setLoadingDias(false);
      }
    };
    carregarDiasFechados();
  }, [filialAtual]);

  // Carregar avisos do Supabase
  useEffect(() => {
    const carregarAvisosInicio = async () => {
      try {
        const dados = await AvisosService.buscarTodas(filialAtual);
        setAvisos(dados);
      } catch (error) {
        console.error('Erro ao carregar avisos:', error);
      }
    };
    carregarAvisosInicio();
  }, [filialAtual]);

  // Carregar termos e regras do backend
  useEffect(() => {
    const carregarTermosRegras = async () => {
      try {
        const dados = await ConfiguracoesService.buscarTodas(filialAtual);
        const termosTitulo = dados.find(c => c.chave === 'termos_regras_titulo');
        const termosTexto = dados.find(c => c.chave === 'termos_regras_texto');
        const postos = dados.find(c => c.chave === 'postos_fiscais');
        const horarioAtendimento = dados.find(c => c.chave === 'horario_atendimento');
        const horarioRecebimentoNF = dados.find(c => c.chave === 'horario_recebimento_nf');
        
        setTermosRegras({
          titulo: termosTitulo?.valor || 'Regras obrigatórias para solicitação de agendamento',
          texto: termosTexto?.valor || '',
          postosFiscais: postos?.valor || ''
        });

        // Extrair horário de funcionamento (formato: "Segunda a Sexta: 08:00 às 18:00")
        if (horarioAtendimento?.valor) {
          const match = horarioAtendimento.valor.match(/(\d{2}:\d{2})\s*às\s*(\d{2}:\d{2})/);
          if (match) {
            setHorarioFuncionamento(`${match[1]} às ${match[2]}`);
          }
        }

        // Extrair horário de recebimento de NF (formato: "Segunda a Sexta: 08:00 às 17:00")
        if (horarioRecebimentoNF?.valor) {
          const match = horarioRecebimentoNF.valor.match(/(\d{2}:\d{2})\s*às\s*(\d{2}:\d{2})/);
          if (match) {
            setHorarioRecebimentoNF(`${match[1]} às ${match[2]}`);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar termos e regras:', error);
      }
    };
    carregarTermosRegras();
  }, [filialAtual]);

  // Carregar solicitações do Supabase
  useEffect(() => {
    const carregarSolicitacoes = async () => {
      try {
        setLoadingSolicitacoes(true);
        // Buscar todas as solicitações do usuário atual
        const dados = await SolicitacoesService.buscarTodas({
          solicitanteId: user?.id,
          filial: filialAtual
        });

        // Mapear dados para o formato esperado pelo componente
        const solicitacoesMapeadas = dados.map(sol => ({
          id: sol.id,
          codigo: `SOL-${sol.id.substring(0, 6)}`,
          cliente: sol.fornecedor || 'Não informado',
          data_desejada: sol.data_desejada || sol.data_solicitacao,
          produto: sol.tipo_produto || 'Não informado',
          quantidade: sol.peso || 0,
          volume: sol.volume || '',
          tipo_carga: sol.tipo_carga || '',
          status: sol.status || 'Solicitado',
          observacoes: sol.observacoes || '',
          criado_em: sol.data_solicitacao,
          criado_por: sol.solicitante_nome || user?.nome || 'Sistema',
          conferente: sol.conferente || '',
          filial: sol.filial || '',
          ordem_chegada: sol.ordem_chegada || null,
          hora_ordem_chegada: sol.hora_ordem_chegada || null,
          documentos: sol.documentos || [],
          reagendamento_proposto: sol.reagendamento_proposto || null,
          mercadoria_a_enviar: sol.mercadoria_a_enviar || ''
        }));

        setSolicitacoes(solicitacoesMapeadas);
      } catch (error) {
        console.error('Erro ao carregar solicitações:', error);
        // Em caso de erro, deixar array vazio
        setSolicitacoes([]);
      } finally {
        setLoadingSolicitacoes(false);
      }
    };
    carregarSolicitacoes();
  }, [filialAtual, user?.id]);

  // Funções para manipulação de documentos
  const handleFileChange = (index: number, file: File | undefined) => {
    const novosDocumentos = [...formData.documentos];
    if (file && file.name) { // Verificar se o arquivo tem nome (não está vazio)
      // Validar tamanho do arquivo (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 10MB');
        return;
      }
      // Validar tipo do arquivo
      if (!file.name.toLowerCase().endsWith('.pdf') && !file.name.toLowerCase().endsWith('.xml')) {
        alert('Apenas arquivos PDF ou XML são permitidos');
        return;
      }
      novosDocumentos[index] = { ...novosDocumentos[index], file };
    }
    setFormData({ ...formData, documentos: novosDocumentos });
  };

  const handleMercadoriaChange = (index: number, fornecedorMercadoria: string) => {
    const novosDocumentos = [...formData.documentos];
    novosDocumentos[index] = { ...novosDocumentos[index], fornecedorMercadoria };
    setFormData({ ...formData, documentos: novosDocumentos });
  };

  const handleAddDocumento = () => {
    setFormData({
      ...formData,
      documentos: [...formData.documentos, { file: new File([], ''), fornecedorMercadoria: '' }]
    });
  };

  const handleRemoveDocumento = (index: number) => {
    const novosDocumentos = formData.documentos.filter((_, i) => i !== index);
    setFormData({ ...formData, documentos: novosDocumentos });
  };


  const getDiasDoMes = (data: Date) => {
    const ano = data.getFullYear();
    const mes = data.getMonth();
    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const dias = [];
    
    // Adicionar dias vazios antes do primeiro dia do mês
    for (let i = 0; i < primeiroDia.getDay(); i++) {
      dias.push(null);
    }
    
    // Adicionar todos os dias do mês
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      dias.push(dia);
    }
    
    return dias;
  };

  const getStatusDia = (dia: number) => {
    const dataStr = `${mesAtual.getFullYear()}-${String(mesAtual.getMonth() + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const fechamentos = diasFechados.filter(d => d.data === dataStr && d.filial_id === filialAtual);
    
    if (fechamentos.length > 0) {
      return {
        fechado: true,
        tipo: fechamentos[0].tipo_fechamento,
        motivo: fechamentos[0].motivo,
        fechamentos
      };
    }
    
    return { fechado: false, tipo: undefined, motivo: undefined, fechamentos: [] };
  };

  const getFechamentosDia = (dataStr: string) => {
    return diasFechados.filter(d => d.data === dataStr && d.filial_id === filialAtual);
  };

  const getCorDia = (dia: number) => {
    const hoje = new Date();
    const dataDia = new Date(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const diaSemana = dataDia.getDay(); // 0 = domingo, 6 = sábado

    // Hoje - borda amarela
    if (dataDia.toDateString() === hoje.toDateString()) {
      return 'border-2 border-yellow-400';
    }

    const status = getStatusDia(dia);
    const tiposFechados = status.fechamentos?.map(f => f.tipo_fechamento) || [];

    // Fins de semana (sábado e domingo) - vermelho (fechado)
    if (diaSemana === 0 || diaSemana === 6) {
      return 'bg-red-500 text-white font-bold';
    }

    // Dias passados
    if (dataDia < hoje && dataDia.toDateString() !== hoje.toDateString()) {
      return 'bg-gray-100 text-gray-500';
    }

    // Dias fechados
    if (status.fechado) {
      if (tiposFechados.includes('completo')) {
        return 'bg-red-500 text-white';
      } else if (tiposFechados.includes('feriado')) {
        // Feriado - cor dourada com cadeado
        return 'bg-yellow-500 text-white font-bold';
      } else {
        // Secos, refrigerados e congelados fechados permanecem verdes
        return 'bg-green-500 text-white hover:bg-green-600 cursor-pointer';
      }
    }

    // Dias disponíveis - verde
    return 'bg-green-500 text-white hover:bg-green-600 cursor-pointer';
  };

  const mudarMes = (delta: number) => {
    const novoMes = new Date(mesAtual);
    novoMes.setMonth(novoMes.getMonth() + delta);
    setMesAtual(novoMes);
  };

  const formatarNomeMes = (data: Date) => {
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${meses[data.getMonth()]} ${data.getFullYear()}`;
  };

  const handleDiaClick = (dia: number) => {
    const dataStr = `${mesAtual.getFullYear()}-${String(mesAtual.getMonth() + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const status = getStatusDia(dia);

    if (podeFecharDia) {
      setDiaSelecionado(dataStr);
      setShowModalFecharDia(true);
    }
  };

  const handleFecharDia = async (tipo: 'completo' | 'secos' | 'refrigerados' | 'congelados' | 'feriado', motivo: string) => {
    if (!diaSelecionado) return;
    
    try {
      const tipoFechamento = tipo;
      
      // Usar API intermediária segura para fechar dia
      const response = await fetch(`${process.env.REACT_APP_API_URL}/fechamento-cargas/fechar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('@sistema-up:token')}`,
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        },
        body: JSON.stringify({
          data: diaSelecionado,
          filial_id: filialAtual,
          tipo_fechamento: tipoFechamento,
          motivo: motivo,
          observacoes: motivo
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        console.error('Erro ao fechar dia:', result);
        alert(`Erro ao fechar dia: ${result.error || 'Erro desconhecido'}`);
        return;
      }
      
      // Recarregar dias fechados usando API intermediária
      const diasResponse = await fetch(`${process.env.REACT_APP_API_URL}/fechamento-cargas/listar/${filialAtual}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('@sistema-up:token')}`,
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        }
      });
      
      if (diasResponse.ok) {
        const diasData = await diasResponse.json();
        setDiasFechados(diasData.data || []);
      }
      
      alert(`${tipo.toUpperCase()} fechado com sucesso!`);
      
    } catch (error) {
      console.error('Erro ao fechar dia:', error);
      alert('Erro ao fechar dia');
    }
  };

  const handleReabrirDia = async (tipoEspecifico?: string) => {
    if (!diaSelecionado) return;
    
    try {
      if (tipoEspecifico) {
        await SolicitacoesService.removerDiaFechado(diaSelecionado, filialAtual, tipoEspecifico);
      } else {
        const tipos = ['completo', 'secos', 'refrigerados', 'congelados', 'feriado'];
        for (const tipo of tipos) {
          await SolicitacoesService.removerDiaFechado(diaSelecionado, filialAtual, tipo);
        }
      }
      
      // Recarregar dias fechados
      const dados = await SolicitacoesService.buscarDiasFechados(filialAtual);
      setDiasFechados(dados);
      
      // Verificar se ainda há fechamentos para este dia
      const aindaFechado = dados.some(d => d.data === diaSelecionado && d.filial_id === filialAtual);
      if (!aindaFechado) {
        setShowModalFecharDia(false);
        setDiaSelecionado(undefined);
      }
      alert(tipoEspecifico 
        ? `${tipoEspecifico.toUpperCase()} reaberto com sucesso!` 
        : 'Dia reaberto com sucesso!');
    } catch (error) {
      console.error('Erro ao reabrir dia:', error);
      alert('Erro ao reabrir dia');
    }
  };

  const handleAceitarReagendamento = async (id: string) => {
    if (!window.confirm('Deseja aceitar o reagendamento? A solicitação irá para o status "Aguardando Pedido" aguardando digitação do pedido.')) return;
    try {
      const { error } = await SolicitacoesService.aceitarReagendamento(id);
      if (error) {
        alert(error.message || 'Erro ao aceitar reagendamento');
        return;
      }
      alert('Reagendamento aceito! Solicitação aguardando digitação de pedido.');
      // Recarregar solicitações
      const dados = await SolicitacoesService.buscarTodas({ solicitanteId: user?.id, filial: filialAtual });
      const solicitacoesMapeadas = dados.map(sol => ({
        id: sol.id,
        codigo: `SOL-${sol.id.substring(0, 6)}`,
        cliente: sol.fornecedor || 'Não informado',
        data_desejada: sol.data_desejada || sol.data_solicitacao,
        produto: sol.tipo_produto || 'Não informado',
        quantidade: sol.peso || 0,
        volume: sol.volume || '',
        tipo_carga: sol.tipo_carga || '',
        status: sol.status || 'Solicitado',
        observacoes: sol.observacoes || '',
        criado_em: sol.data_solicitacao,
        criado_por: sol.solicitante_nome || user?.nome || 'Sistema',
        conferente: sol.conferente || '',
        filial: sol.filial || '',
        ordem_chegada: sol.ordem_chegada || null,
        hora_ordem_chegada: sol.hora_ordem_chegada || null,
        documentos: sol.documentos || [],
        reagendamento_proposto: sol.reagendamento_proposto || null,
        mercadoria_a_enviar: sol.mercadoria_a_enviar || ''
      }));
      setSolicitacoes(solicitacoesMapeadas);
    } catch (error) {
      console.error('Erro ao aceitar reagendamento:', error);
      alert('Erro ao aceitar reagendamento');
    }
  };

  const handleRejeitarReagendamento = async (id: string) => {
    const motivo = window.prompt('Motivo da rejeição do reagendamento:');
    if (!motivo) return;
    try {
      const { error } = await SolicitacoesService.rejeitarReagendamento(id, motivo);
      if (error) {
        alert(error.message || 'Erro ao rejeitar reagendamento');
        return;
      }
      alert('Reagendamento rejeitado. A solicitação voltou para status Pendente.');
      // Recarregar solicitações
      const dados = await SolicitacoesService.buscarTodas({ solicitanteId: user?.id, filial: filialAtual });
      const solicitacoesMapeadas = dados.map(sol => ({
        id: sol.id,
        codigo: `SOL-${sol.id.substring(0, 6)}`,
        cliente: sol.fornecedor || 'Não informado',
        data_desejada: sol.data_desejada || sol.data_solicitacao,
        produto: sol.tipo_produto || 'Não informado',
        quantidade: sol.peso || 0,
        volume: sol.volume || '',
        tipo_carga: sol.tipo_carga || '',
        status: sol.status || 'Solicitado',
        observacoes: sol.observacoes || '',
        criado_em: sol.data_solicitacao,
        criado_por: sol.solicitante_nome || user?.nome || 'Sistema',
        conferente: sol.conferente || '',
        filial: sol.filial || '',
        ordem_chegada: sol.ordem_chegada || null,
        hora_ordem_chegada: sol.hora_ordem_chegada || null,
        documentos: sol.documentos || [],
        reagendamento_proposto: sol.reagendamento_proposto || null,
        mercadoria_a_enviar: sol.mercadoria_a_enviar || ''
      }));
      setSolicitacoes(solicitacoesMapeadas);
    } catch (error) {
      console.error('Erro ao rejeitar reagendamento:', error);
      alert('Erro ao rejeitar reagendamento');
    }
  };

  const formatarData = (dataString: string | undefined) => {
    if (!dataString) return '';
    // Converter string para Date usando fuso horário local
    // Tenta diferentes formatos de data
    if (dataString.includes('T')) {
      // Formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
      // Formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
      const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    } else {
      // Formato simples (YYYY-MM-DD)
      const [ano, mes, dia] = dataString.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    }
  };

  const formatarTamanho = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleVerDocumentos = (solicitacao: any) => {
    setSolicitacaoDocumentos(solicitacao);
    setShowDocumentosModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Solicitado': return 'bg-gray-100 text-gray-800';
      case 'Aguardando pedido': return 'bg-yellow-100 text-yellow-800';
      case 'Pedido digitado': return 'bg-blue-100 text-blue-800';
      case 'Aprovado': return 'bg-green-100 text-green-800';
      case 'Chegou': return 'bg-purple-100 text-purple-800';
      case 'Descarregando': return 'bg-orange-100 text-orange-800';
      case 'Finalizado': return 'bg-emerald-100 text-emerald-800';
      case 'Pendente': return 'bg-blue-100 text-blue-800';
      case 'Rejeitado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const solicitacoesFiltradas = solicitacoes.filter(s => {
    // Filtro por finalizadas
    if (ocultarFinalizadas && ['Finalizado', 'Rejeitado'].includes(s.status)) {
      return false;
    }

    // Filtro por busca
    if (buscaSolicitacoes) {
      const termoBusca = buscaSolicitacoes.toLowerCase();
      return (
        s.codigo.toLowerCase().includes(termoBusca) ||
        s.id.toLowerCase().includes(termoBusca) ||
        s.cliente.toLowerCase().includes(termoBusca) ||
        s.produto.toLowerCase().includes(termoBusca) ||
        s.status.toLowerCase().includes(termoBusca) ||
        formatarData(s.data_desejada).includes(termoBusca)
      );
    }

    // Filtro por data específica
    if (buscaData) {
      const dataFormatada = formatarData(s.data_desejada);
      return dataFormatada === formatarData(buscaData);
    }

    return true;
  });

  const avisosBanner = avisos.filter(a => a.lado === 'esquerdo');
  const textoBanner = avisosBanner.map(a => `${a.titulo}: ${a.mensagem}`).join('   •   ');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 invisible h-0">Início</h1>

        {/* Banner de Avisos Importantes — acima do calendário, abaixo do seletor de filial */}
        {avisosBanner.length > 0 && (
          <div className="mb-4 rounded-lg overflow-hidden shadow-lg border border-warning-300">
            <div className="flex items-stretch">
              {/* Faixa lateral com ícone */}
              <div className="flex items-center justify-center bg-gradient-to-b from-warning-500 to-warning-600 px-3 sm:px-4 py-2 flex-shrink-0">
                <div className="flex items-center gap-2 text-white">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 animate-pulse-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317zM22 5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="hidden sm:inline font-bold text-sm uppercase tracking-wide whitespace-nowrap">Avisos</span>
                </div>
              </div>

              {/* Conteúdo com scroll (marquee) */}
              <div className="flex-1 bg-gradient-to-r from-warning-50 to-warning-100 overflow-hidden relative">
                <div className="flex whitespace-nowrap animate-marquee py-2.5 sm:py-3">
                  <span className="text-sm sm:text-base font-medium text-warning-800 px-4">
                    {textoBanner}
                  </span>
                  <span className="text-sm sm:text-base font-medium text-warning-800 px-4" aria-hidden="true">
                    {textoBanner}
                  </span>
                </div>
                {/* Gradiente nas bordas para efeito de fade */}
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-warning-50 to-transparent pointer-events-none"></div>
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-warning-100 to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>
        )}

        {/* Calendário de Agendamento */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <button 
              onClick={() => mudarMes(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="text-center">
              <h2 className="text-xl font-bold">{getNomeAgenda()}</h2>
              <p className="text-sm text-gray-500">Horário de Funcionamento: {horarioFuncionamento}</p>
              <p className="text-sm text-gray-500">Horário de Recebimento de NF: {horarioRecebimentoNF}</p>
            </div>
            
            <button 
              onClick={() => mudarMes(1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Legenda */}
          <div className="flex justify-center gap-4 mb-4 text-sm flex-wrap">
            <span className="flex items-center">
              <span className="w-3 h-3 bg-green-500 rounded mr-1"></span>
              Dia livre (vagas disponíveis)
            </span>
            <span className="flex items-center">
              <span className="w-3 h-3 bg-red-500 rounded mr-1"></span>
              Dia fechado (sem vagas)
            </span>
            <span className="flex items-center">
              <span className="w-3 h-3 border-2 border-yellow-400 rounded mr-1"></span>
              Hoje
            </span>
            <span className="flex items-center">
              <span className="text-white mr-1 bg-red-500 px-1 rounded flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs ml-1">S</span>
              </span>
              Secos fechados
            </span>
            <span className="flex items-center">
              <span className="text-white mr-1 bg-blue-500 px-1 rounded flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs ml-1">R</span>
              </span>
              Refrigerados fechados
            </span>
            <span className="flex items-center">
              <span className="text-white mr-1 bg-cyan-500 px-1 rounded flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs ml-1">C</span>
              </span>
              Congelados fechados
            </span>
            <span className="flex items-center">
              <span className="text-white mr-1 bg-yellow-500 px-1 rounded flex items-center justify-center">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C11.5 2 11 2.19 10.59 2.59L2.59 10.59C1.8 11.37 1.8 12.63 2.59 13.41L10.59 21.41C11.37 22.2 12.63 22.2 13.41 21.41L21.41 13.41C22.2 12.63 22.2 11.37 21.41 10.59L13.41 2.59C13 2.19 12.5 2 12 2M12 4L20 12L12 20L4 12L12 4M12 6.5C10.07 6.5 8.5 8.07 8.5 10C8.5 10.45 8.62 10.88 8.82 11.25L6.25 13.82C5.85 14.21 5.85 14.85 6.25 15.25C6.64 15.64 7.28 15.64 7.67 15.25L10.25 12.68C10.62 12.88 11.05 13 11.5 13C13.43 13 15 11.43 15 9.5C15 8.05 13.93 6.84 12.5 6.5M11.5 8.5C12.05 8.5 12.5 8.95 12.5 9.5C12.5 10.05 12.05 10.5 11.5 10.5C10.95 10.5 10.5 10.05 10.5 9.5C10.5 8.95 10.95 8.5 11.5 8.5Z" />
                </svg>
                <span className="text-xs ml-1">🔒</span>
              </span>
              Feriado
            </span>
          </div>

          {/* Calendário */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center">
            {/* Dias da semana */}
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dia => (
              <div key={dia} className="font-semibold text-gray-600 p-1 sm:p-2 text-xs sm:text-sm">
                {dia}
              </div>
            ))}
            
            {/* Dias do mês */}
            {getDiasDoMes(mesAtual).map((dia, index) => {
              if (dia === null) {
                return <div key={`empty-${index}`} className="p-1 sm:p-2"></div>;
              }
              
              const status = getStatusDia(dia);
              const cor = getCorDia(dia);
              
              return (
                <div
                  key={dia}
                  onClick={() => handleDiaClick(dia)}
                  className={`p-1 sm:p-2 text-xs sm:text-sm rounded-lg font-medium ${cor} transition-colors`}
                  title={status.fechado ? status.fechamentos?.map((f: any) => `${f.tipo_fechamento}: ${f.motivo || ''}`).join(' | ') : 'Clique para fechar o dia'}
                >
                  <div>{dia}</div>
                  {status.fechado && status.fechamentos?.map((fech, idx) => {
                    if (fech.tipo_fechamento === 'completo') {
                      return (
                        <div key={idx} className="hidden sm:flex items-center justify-center mt-0.5">
                          <span className="text-white bg-red-500 px-1 rounded text-xs font-bold">Fechado</span>
                        </div>
                      );
                    }
                    if (fech.tipo_fechamento === 'feriado') {
                      return (
                        <div key={idx} className="flex items-center justify-center mt-0.5">
                          <span className="text-white bg-yellow-500 px-1 rounded flex items-center justify-center">
                            <svg className="w-2 h-2 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2C11.5 2 11 2.19 10.59 2.59L2.59 10.59C1.8 11.37 1.8 12.63 2.59 13.41L10.59 21.41C11.37 22.2 12.63 22.2 13.41 21.41L21.41 13.41C22.2 12.63 22.2 11.37 21.41 10.59L13.41 2.59C13 2.19 12.5 2 12 2M12 4L20 12L12 20L4 12L12 4M12 6.5C10.07 6.5 8.5 8.07 8.5 10C8.5 10.45 8.62 10.88 8.82 11.25L6.25 13.82C5.85 14.21 5.85 14.85 6.25 15.25C6.64 15.64 7.28 15.64 7.67 15.25L10.25 12.68C10.62 12.88 11.05 13 11.5 13C13.43 13 15 11.43 15 9.5C15 8.05 13.93 6.84 12.5 6.5M11.5 8.5C12.05 8.5 12.5 8.95 12.5 9.5C12.5 10.05 12.05 10.5 11.5 10.5C10.95 10.5 10.5 10.05 10.5 9.5C10.5 8.95 10.95 8.5 11.5 8.5Z" />
                            </svg>
                            <span className="text-xs ml-1 font-bold">🔒</span>
                          </span>
                        </div>
                      );
                    }
                    const config: Record<string, { bg: string; label: string }> = {
                      secos: { bg: 'bg-red-500', label: 'S' },
                      refrigerados: { bg: 'bg-blue-500', label: 'R' },
                      congelados: { bg: 'bg-cyan-500', label: 'C' },
                    };
                    const cfg = config[fech.tipo_fechamento];
                    if (!cfg) return null;
                    return (
                      <div key={idx} className="flex items-center justify-center mt-0.5">
                        <span className={`text-white ${cfg.bg} px-1 rounded flex items-center justify-center`}>
                          <svg className="w-2 h-2 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-xs ml-1 font-bold">{cfg.label}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Botão Solicitar Agendamento — centralizado abaixo do calendário */}
        <div className="mb-6 flex justify-center">
          {!carregandoPermissao && podeSolicitarAgendamento && (
            <MagneticButton
              label="Solicitar Agendamento"
              onClick={() => {
                setFormData(prev => ({ ...prev, fornecedor: prev.fornecedor || user?.nome || '' }));
                setShowModalSolicitacao(true);
              }}
              fill="#334e68"
              textColor="#ffffff"
              sweepColor="#f97316"
              sweepTextColor="#ffffff"
              border={false}
              radius={12}
              paddingX={32}
              paddingY={16}
              magnet={8}
              font={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: 15,
                lineHeight: "1em",
                letterSpacing: "-0.01em",
                textAlign: "center",
              }}
            />
          )}
        </div>

        {/* Minhas Solicitações */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="font-semibold text-gray-900">Minhas Solicitações de Agendamento</h3>
            <div className="flex flex-col md:flex-row items-center gap-3">
              <input
                type="text"
                placeholder="Buscar por código, ID, fornecedor..."
                value={buscaSolicitacoes}
                onChange={(e) => setBuscaSolicitacoes(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="date"
                value={buscaData}
                onChange={(e) => setBuscaData(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={ocultarFinalizadas}
                  onChange={(e) => setOcultarFinalizadas(e.target.checked)}
                  className="mr-2"
                />
                Ocultar finalizadas
              </label>
            </div>
          </div>
          
          <div className="space-y-4">
            {solicitacoesFiltradas.map(solicitacao => (
              <div key={solicitacao.id} className="p-4 border rounded-lg">
                {/* Destaque de Ordem de Chegada */}
                {solicitacao.ordem_chegada && (
                  <div className="mb-3 bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold text-blue-600">
                          #{solicitacao.ordem_chegada}
                        </div>
                        <div className="text-sm text-gray-700">
                          <div className="font-medium">Ordem de Chegada</div>
                          <div>Horário: {solicitacao.hora_ordem_chegada}</div>
                        </div>
                      </div>
                      <div className="text-xs text-blue-600 font-semibold">
                        🚚 CARGA NA FILA
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="font-medium text-lg">{solicitacao.codigo}</div>
                      <div className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        ID: {solicitacao.id}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">{solicitacao.cliente}</div>
                    <div className="text-sm text-gray-600">
                      {solicitacao.produto} - {solicitacao.quantidade.toLocaleString('pt-BR')} kg
                    </div>
                    {solicitacao.mercadoria_a_enviar && (
                      <div className="text-sm text-gray-600">
                        Mercadoria: {solicitacao.mercadoria_a_enviar}
                      </div>
                    )}
                    <div className="text-sm text-gray-600">
                      Volume: {solicitacao.volume || 'Não informado'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Tipo de carga: {solicitacao.tipo_carga || 'Não informado'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Data desejada: {formatarData(solicitacao.data_desejada)}
                    </div>
                    <div className="text-sm text-gray-600">
                      Data solicitação: {formatarData(solicitacao.criado_em)}
                    </div>
                    <div className="text-sm text-gray-600">
                      Filial: {solicitacao.filial || 'Não informada'}
                    </div>
                    {solicitacao.conferente && (
                      <div className="text-sm text-gray-600">
                        Conferente: {solicitacao.conferente}
                      </div>
                    )}
                    {solicitacao.observacoes && (
                      <div className="text-sm text-gray-500 mt-1">{solicitacao.observacoes}</div>
                    )}
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(solicitacao.status)}`}>
                    {solicitacao.status}
                  </span>
                </div>

                {/* Fluxo de etapas */}
                <div className="mt-4">
                  <StatusFlow currentStatus={solicitacao.status} />
                </div>
                
                {/* Exibir proposta de reagendamento se houver */}
                {solicitacao.status === 'REAGENDADO' && solicitacao.reagendamento_proposto && (
                  <div className="mt-4 bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-orange-900">📅 Proposta de Reagendamento</p>
                        <p className="text-sm text-orange-800 mt-1">
                          <strong>Nova Data:</strong> {formatarData(solicitacao.reagendamento_proposto.novaData)}
                        </p>
                        <p className="text-sm text-orange-800">
                          <strong>Motivo:</strong> {solicitacao.reagendamento_proposto.motivo}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => handleAceitarReagendamento(solicitacao.id)}
                        variant="primary"
                        size="sm"
                      >
                        ✅ Aceitar
                      </Button>
                      <Button
                        onClick={() => handleRejeitarReagendamento(solicitacao.id)}
                        variant="outline"
                        size="sm"
                      >
                        ❌ Rejeitar
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Botão de Comprovante - aparece quando aprovado ou status posteriores */}
                {['aprovado', 'chegou', 'descarregando', 'finalizado'].includes(solicitacao.status.toLowerCase()) && (
                  <div className="mt-6">
                    <Button
                      onClick={async () => {
                        const sucesso = await ComprovanteService.gerarComprovanteAgendamento(solicitacao);
                        if (!sucesso) {
                          alert('Erro ao gerar comprovante. Tente novamente.');
                        }
                      }}
                      className="w-full"
                      variant="success"
                    >
                      📄 Baixar Comprovante
                    </Button>
                  </div>
                )}

                {/* Botão de Documentos - aparece se houver documentos */}
                {solicitacao.documentos && solicitacao.documentos.length > 0 && (
                  <div className="mt-4">
                    <Button
                      onClick={() => handleVerDocumentos(solicitacao)}
                      className="w-full"
                      variant="outline"
                    >
                      📎 Ver Documentos ({solicitacao.documentos.length})
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            {solicitacoesFiltradas.length === 0 && (
              <div className="text-gray-500 text-center py-4">
                {ocultarFinalizadas ? 'Nenhuma solicitação pendente' : 'Nenhuma solicitação encontrada'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Solicitação de Agendamento */}
      <Modal
        isOpen={showModalSolicitacao}
        onClose={() => {
          setShowModalSolicitacao(false);
          setTermosAceitos(false);
        }}
        title="Solicitar Agendamento"
        size="lg"
      >
        <div className="space-y-6">
          {!termosAceitos ? (
            // Seção de Termos e Regras
            <div className="bg-gray-50 border-l-4 border-gray-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-gray-600 text-xl">!</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-800">
                    {termosRegras.titulo}
                  </h3>
                  <div className="mt-2 text-sm text-gray-700">
                    {termosRegras.texto ? (
                      <div dangerouslySetInnerHTML={{ __html: termosRegras.texto.replace(/\n/g, '<br />') }} />
                    ) : (
                      <p>Antes de solicitar o agendamento, confira com atenção as orientações abaixo:</p>
                    )}
                    
                    {!termosRegras.texto && (
                      <div className="mt-3 space-y-2">
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <strong className="text-gray-800">1) Fornecedor fora do estado - passagem obrigatória em posto fiscal</strong>
                          <p className="text-gray-600 mt-1">Toda carga de fornecedor localizado fora de Alagoas deve passar por um dos postos fiscais:</p>
                          <ul className="text-gray-600 mt-1 ml-4 list-disc text-sm">
                            <li>Delmiro Gouveia - Rodovia AL 423, KM 101</li>
                            <li>Novo Lino - Rodovia BR 101 - KM 0</li>
                            <li>Porto Real do Colégio - Rodovia BR 101 - KM 245</li>
                            <li>São José da Laje - Rodovia BR 104 - KM 12</li>
                          </ul>
                        </div>
                        
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <strong className="text-gray-800">2) Código de barras obrigatório (DUN e EAN)</strong>
                          <p className="text-gray-600 mt-1">Todos os produtos devem possuir código DUN e EAN, sem reutilização de códigos.</p>
                        </div>
                        
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <strong className="text-gray-800">3) Mudança de fator</strong>
                          <p className="text-gray-600 mt-1">Qualquer alteração de fator deve ser informada previamente aos setores de Compras e Logística.</p>
                        </div>
                      </div>
                    )}
                    
                    <p className="mt-3 font-medium text-gray-800">
                      O descumprimento pode acarretar em reagendamento ou devolução da carga.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={termosAceitos}
                    onChange={(e) => setTermosAceitos(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Li e concordo com as regras acima para prosseguir com a solicitação
                  </span>
                </label>
              </div>
              
              <Button
                onClick={() => setTermosAceitos(true)}
                disabled={!termosAceitos}
                className="w-full"
              >
                Aceito os termos e desejo continuar
              </Button>
            </div>
          ) : (
            // Formulário de Solicitação
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Solicite um agendamento para uma nova carga. Campos marcados com * são obrigatórios.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor *
                  </label>
                  <input
                    type="text"
                    value={formData.fornecedor}
                    onChange={(e) => setFormData({...formData, fornecedor: e.target.value})}
                    disabled={user?.perfil === 'fornecedor'}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    placeholder="Nome do fornecedor"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mercadorias Enviadas
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                    rows={3}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="Descrição das mercadorias enviadas"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peso Estimado (kg) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.peso}
                    onChange={(e) => setFormData({...formData, peso: e.target.value})}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Volume Estimado *
                  </label>
                  <input
                    type="number"
                    value={formData.volume}
                    onChange={(e) => setFormData({...formData, volume: e.target.value})}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Produto * <span className="text-red-500">(SECOS, REFRIGERADOS ou CONGELADOS)</span>
                  </label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipoProduto"
                        value="SECOS"
                        checked={formData.tipoProduto === 'SECOS'}
                        onChange={(e) => setFormData({...formData, tipoProduto: e.target.value})}
                        className="mr-2"
                      />
                      SECOS
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipoProduto"
                        value="REFRIGERADOS"
                        checked={formData.tipoProduto === 'REFRIGERADOS'}
                        onChange={(e) => setFormData({...formData, tipoProduto: e.target.value})}
                        className="mr-2"
                      />
                      REFRIGERADOS
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipoProduto"
                        value="CONGELADOS"
                        checked={formData.tipoProduto === 'CONGELADOS'}
                        onChange={(e) => setFormData({...formData, tipoProduto: e.target.value})}
                        className="mr-2"
                      />
                      CONGELADOS
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Carga *
                  </label>
                  <select
                    value={formData.tipoCarga}
                    onChange={(e) => setFormData({...formData, tipoCarga: e.target.value})}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Selecione o tipo de carga</option>
                    <option value="PALETIZADO">Paletizado</option>
                    <option value="BATIDO">Batido</option>
                    <option value="MISTO">Misto</option>
                  </select>
                </div>
                
                <div>
                  <DatePicker
                    label="Data Desejada *"
                    value={formData.dataDesejada}
                    onChange={(date) => setFormData({...formData, dataDesejada: date ? date.toLocaleDateString('en-CA') + 'T00:00:00-03:00' : ''})}
                  />
                </div>
              </div>
              
              {/* Seção de Documentos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Documentos *
                </label>
                <div className="space-y-3">
                  {formData.documentos.map((doc, index) => (
                    <div key={index} className="border rounded-lg p-3 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Documento {index + 1} *
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="file"
                              accept=".pdf,.xml"
                              onChange={(e) => handleFileChange(index, e.target.files?.[0])}
                              className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            <span className="text-xs text-gray-500">PDF, XML (Máx: 10MB)</span>
                          </div>
                          {doc.file && doc.file.name && (
                            <div className="mt-1 text-sm text-green-600">
                              Arquivo selecionado: {doc.file.name}
                            </div>
                          )}
                        </div>
                        
                                              </div>
                      
                      {formData.documentos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveDocumento(index)}
                          className="mt-2 text-sm text-red-600 hover:text-red-800"
                        >
                          Remover documento
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={handleAddDocumento}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Adicionar outro documento
                  </button>
                </div>
              </div>
              
                            
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowModalSolicitacao(false)}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  disabled={enviandoSolicitacao}
                  onClick={async () => {
                    if (enviandoSolicitacao) return;
                    
                    setEnviandoSolicitacao(true);
                    
                    try {
                      // Validar campos obrigatórios
                      if (!formData.fornecedor || !formData.peso || !formData.volume || !formData.tipoProduto || !formData.tipoCarga || !formData.dataDesejada || !formData.documentos || formData.documentos.length === 0) {
                        setAlertModal({
                          open: true,
                          type: 'warning',
                          title: 'Campos obrigatórios',
                          message: 'Por favor, preencha todos os campos obrigatórios!',
                          confirmText: 'Ok',
                        });
                        setEnviandoSolicitacao(false);
                        return;
                      }

                      // Validar se há arquivos selecionados (não apenas o estado inicial)
                      const documentosValidos = formData.documentos.filter(doc => doc.file && doc.file.name);
                      if (documentosValidos.length === 0) {
                        setAlertModal({
                          open: true,
                          type: 'warning',
                          title: 'Documentação necessária',
                          message: 'Por favor, anexe pelo menos um documento!',
                          confirmText: 'Ok',
                        });
                        setEnviandoSolicitacao(false);
                        return;
                      }

                      // Validar dia fechado
                      const validacaoDia = await ValidationService.validarDiaFechado(
                        formData.dataDesejada,
                        filialAtual,
                        formData.tipoProduto
                      );
                      
                      if (!validacaoDia.isValid) {
                        setAlertModal({
                          open: true,
                          type: 'warning',
                          title: 'Oops! Dia indisponível',
                          messages: validacaoDia.errors,
                          confirmText: 'Escolher outra data',
                        });
                        setEnviandoSolicitacao(false);
                        return;
                      }
                      
                      // Criar solicitação no Supabase
                      // Gerar ID único para a solicitação
                      const idSolicitacao = crypto.randomUUID();
                      
                      // Processar documentos se houver
                      const documentosProcessados: any[] = [];
                      let uploadFalhou = false;

                      if (documentosValidos.length > 0) {
                        for (let i = 0; i < documentosValidos.length; i++) {
                          const doc = documentosValidos[i];

                          try {
                            // Fazer upload do documento
                            const uploadResult = await UploadService.uploadFile(
                              doc.file,
                              'documentos',
                              `solicitacao-${idSolicitacao}`
                            );

                            if (uploadResult.success) {
                              documentosProcessados.push({
                                ...doc,
                                url: uploadResult.fileUrl,
                                storagePath: uploadResult.fileName,
                                uploadedAt: new Date().toISOString(),
                                uploadedBy: user?.id,
                                uploadSucesso: true
                              });
                            } else {
                              console.error('❌ Erro no upload:', uploadResult.error);
                              uploadFalhou = true;
                              alert(`Erro ao fazer upload do arquivo ${doc.file.name}: ${uploadResult.error}`);
                              break;
                            }
                          } catch (error) {
                            console.error('❌ Erro exceção no upload:', error);
                            uploadFalhou = true;
                            alert(`Erro ao fazer upload do arquivo ${doc.file.name}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
                            break;
                          }
                        }
                      }

                      // Se algum upload falhou, não criar a solicitação
                      if (uploadFalhou) {
                        setEnviandoSolicitacao(false);
                        return;
                      }

                      const dadosSolicitacao = {
                        id: idSolicitacao,
                        codigo: `SOL-${idSolicitacao.substring(0, 7)}`,
                        solicitante_id: user?.id || '',
                        solicitante_nome: user?.nome || 'Usuário',
                        fornecedor: formData.fornecedor,
                        fornecedor_mercadoria: formData.fornecedor_mercadoria,
                        observacoes: formData.observacoes,
                        data_desejada: formData.dataDesejada,
                        peso: parseFloat(formData.peso),
                        volume: formData.volume,
                        tipo_produto: formData.tipoProduto,
                        tipo_carga: formData.tipoCarga,
                        conferente: '',
                        status: StatusSolicitacao.PENDENTE,
                        data_solicitacao: new Date().toISOString(),
                        data_atualizacao: new Date().toISOString(),
                        filial: filialAtual,
                        documentos: documentosProcessados
                      };
                      
                      const resultado = await SolicitacoesService.criarSolicitacao(dadosSolicitacao);
                      
                      if (resultado.error) {
                        const errMsg = resultado.error.message || '';
                        const isDiaFechado = errMsg.toLowerCase().includes('fechad') || errMsg.toLowerCase().includes('feriado') || errMsg.toLowerCase().includes('não é possível');
                        const errMessages = isDiaFechado
                          ? [errMsg, 'Motivo: A data selecionada não está disponível para agendamento conforme configurado pela filial.', 'O que fazer: Escolha outra data disponível no calendário (dias em verde) ou entre em contato com a filial para mais informações.']
                          : [errMsg];
                        setAlertModal({
                          open: true,
                          type: isDiaFechado ? 'warning' : 'error',
                          title: isDiaFechado ? 'Oops! Dia indisponível' : 'Erro ao criar solicitação',
                          messages: errMessages,
                          confirmText: isDiaFechado ? 'Escolher outra data' : 'Entendi',
                        });
                        setEnviandoSolicitacao(false);
                        return;
                      }
                      
                      if (resultado.data) {
                        setAlertModal({
                          open: true,
                          type: 'success',
                          title: 'Solicitação enviada!',
                          message: `Sua solicitação foi criada com sucesso! ID: ${resultado.data.id}`,
                          confirmText: 'Ok',
                        });

                        // Recarregar solicitações após criar nova
                        const dadosAtualizados = await SolicitacoesService.buscarTodas({
                          solicitanteId: user?.id,
                          filial: filialAtual
                        });

                        const solicitacoesMapeadas = dadosAtualizados.map(sol => ({
                          id: sol.id,
                          codigo: `SOL-${sol.id.substring(0, 6)}`,
                          cliente: sol.fornecedor || 'Não informado',
                          data_desejada: sol.data_desejada || sol.data_solicitacao,
                          produto: sol.tipo_produto || 'Não informado',
                          quantidade: sol.peso || 0,
                          volume: sol.volume || '',
                          tipo_carga: sol.tipo_carga || '',
                          status: sol.status || 'Solicitado',
                          observacoes: sol.observacoes || '',
                          criado_em: sol.data_solicitacao,
                          criado_por: sol.solicitante_nome || user?.nome || 'Sistema',
                          conferente: sol.conferente || '',
                          filial: sol.filial || '',
                          ordem_chegada: sol.ordem_chegada || null,
                          hora_ordem_chegada: sol.hora_ordem_chegada || null
                        }));

                        setSolicitacoes(solicitacoesMapeadas);
                      } else {
                        console.error('Solicitação criada mas sem dados retornados');
                        setAlertModal({
                          open: true,
                          type: 'warning',
                          title: 'Atenção',
                          message: 'Solicitação criada mas houve um erro ao confirmar. Verifique o console.',
                          confirmText: 'Ok',
                        });
                      }

                      setShowModalSolicitacao(false);
                      setTermosAceitos(false);
                      // Resetar formulário
                      setFormData({
                        fornecedor: user?.nome || '',
                        fornecedor_mercadoria: '',
                        peso: '',
                        volume: '',
                        tipoProduto: '',
                        tipoCarga: '',
                        dataDesejada: new Date().toISOString().split('T')[0],
                        observacoes: '',
                        documentos: [{ file: new File([], ''), fornecedorMercadoria: '' }]
                      });
                    } catch (error) {
                      console.error('Erro ao criar solicitação:', error);
                      const errMsg = (error as Error).message || '';
                      const isDiaFechado = errMsg.toLowerCase().includes('fechad') || errMsg.toLowerCase().includes('feriado') || errMsg.toLowerCase().includes('não é possível');
                      const errMessages = isDiaFechado
                        ? [errMsg, 'Motivo: A data selecionada não está disponível para agendamento conforme configurado pela filial.', 'O que fazer: Escolha outra data disponível no calendário (dias em verde) ou entre em contato com a filial para mais informações.']
                        : [errMsg];
                      setAlertModal({
                        open: true,
                        type: isDiaFechado ? 'warning' : 'error',
                        title: isDiaFechado ? 'Oops! Dia indisponível' : 'Erro ao criar solicitação',
                        messages: errMessages,
                        confirmText: isDiaFechado ? 'Escolher outra data' : 'Entendi',
                      });
                    } finally {
                      setEnviandoSolicitacao(false);
                    }
                }}>
                  Enviar Solicitação
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal de Fechar Dia */}
      <Modal
        isOpen={showModalFecharDia}
        onClose={() => setShowModalFecharDia(false)}
        title="Fechar Dia"
        size="md"
      >
        <div className="space-y-4">
          <p>Selecione o tipo de fechamento para o dia {diaSelecionado}:</p>
          <div className="space-y-2">
            {diaSelecionado && (() => {
              const tiposAtivos = getFechamentosDia(diaSelecionado).map(f => f.tipo_fechamento);
              const btnBase = "w-full text-left p-3 border rounded transition-colors";
              const btnDisabled = "opacity-50 cursor-not-allowed bg-gray-100";
              const btnHover = "hover:bg-gray-50";
              return (
                <>
                  <button 
                    onClick={() => !tiposAtivos.includes('completo') && handleFecharDia('completo', 'Fechamento completo - nenhuma carga será aceita')}
                    disabled={tiposAtivos.includes('completo')}
                    className={`${btnBase} ${tiposAtivos.includes('completo') ? btnDisabled : btnHover}`}
                  >
                    <div className="font-medium">Fechamento Completo</div>
                    <div className="text-sm text-gray-600">Nenhuma carga será aceita</div>
                  </button>
                  <button 
                    onClick={() => !tiposAtivos.includes('secos') && handleFecharDia('secos', 'Fechamento parcial - apenas cargas de produtos secos')}
                    disabled={tiposAtivos.includes('secos')}
                    className={`${btnBase} ${tiposAtivos.includes('secos') ? btnDisabled : btnHover}`}
                  >
                    <div className="font-medium">Apenas SECOS {tiposAtivos.includes('secos') && '✓'}</div>
                    <div className="text-sm text-gray-600">Apenas cargas de produtos secos</div>
                  </button>
                  <button 
                    onClick={() => !tiposAtivos.includes('refrigerados') && handleFecharDia('refrigerados', 'Fechamento parcial - apenas cargas de produtos refrigerados')}
                    disabled={tiposAtivos.includes('refrigerados')}
                    className={`${btnBase} ${tiposAtivos.includes('refrigerados') ? btnDisabled : btnHover}`}
                  >
                    <div className="font-medium">Apenas REFRIGERADOS {tiposAtivos.includes('refrigerados') && '✓'}</div>
                    <div className="text-sm text-gray-600">Apenas cargas de produtos refrigerados</div>
                  </button>
                  <button 
                    onClick={() => !tiposAtivos.includes('congelados') && handleFecharDia('congelados', 'Fechamento parcial - apenas cargas de produtos congelados')}
                    disabled={tiposAtivos.includes('congelados')}
                    className={`${btnBase} ${tiposAtivos.includes('congelados') ? btnDisabled : btnHover}`}
                  >
                    <div className="font-medium">Apenas CONGELADOS {tiposAtivos.includes('congelados') && '✓'}</div>
                    <div className="text-sm text-gray-600">Apenas cargas de produtos congelados</div>
                  </button>
                  <button 
                    onClick={() => !tiposAtivos.includes('feriado') && handleFecharDia('feriado', 'Feriado - dia sem expediente')}
                    disabled={tiposAtivos.includes('feriado')}
                    className={`${btnBase} ${tiposAtivos.includes('feriado') ? btnDisabled : 'hover:bg-yellow-50 bg-yellow-50 border-yellow-300'}`}
                  >
                    <div className="font-medium text-yellow-800">🔒 Feriado {tiposAtivos.includes('feriado') && '✓'}</div>
                    <div className="text-sm text-yellow-600">Dia sem expediente</div>
                  </button>
                </>
              );
            })()}
          </div>
          {/* Botões de Reabrir por tipo */}
          {diaSelecionado && getFechamentosDia(diaSelecionado).length > 0 && (
            <div className="border-t pt-4 mt-4">
              <p className="font-medium text-gray-900 mb-2">Reabrir dia (selecione o tipo):</p>
              <div className="flex flex-wrap gap-2">
                {getFechamentosDia(diaSelecionado).map((fech, idx) => {
                  const label = fech.tipo_fechamento === 'completo' ? 'Completo' 
                    : fech.tipo_fechamento === 'secos' ? 'Secos' 
                    : fech.tipo_fechamento === 'refrigerados' ? 'Refrigerados' 
                    : fech.tipo_fechamento === 'congelados' ? 'Congelados' 
                    : fech.tipo_fechamento === 'feriado' ? 'Feriado' 
                    : fech.tipo_fechamento;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleReabrirDia(fech.tipo_fechamento)}
                      className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 transition-colors"
                    >
                      Reabrir {label}
                    </button>
                  );
                })}
                <button
                  onClick={() => handleReabrirDia()}
                  className="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Reabrir Tudo
                </button>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3 mt-4">
            <Button variant="outline" onClick={() => setShowModalFecharDia(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Documentos */}
      <Modal
        isOpen={showDocumentosModal}
        onClose={() => setShowDocumentosModal(false)}
        title="Documentos da Solicitação"
        size="md"
      >
        {solicitacaoDocumentos && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <p><strong>Fornecedor:</strong> {solicitacaoDocumentos.fornecedor || solicitacaoDocumentos.cliente}</p>
              <p><strong>Data Desejada:</strong> {formatarData(solicitacaoDocumentos.data_desejada)}</p>
            </div>
            
            {solicitacaoDocumentos.documentos && solicitacaoDocumentos.documentos.length > 0 ? (
              <div className="space-y-3">
                {solicitacaoDocumentos.documentos.map((doc: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{doc.nome}</p>
                        <p className="text-sm text-gray-500">{doc.tipo}</p>
                        <p className="text-xs text-gray-400">
                          {doc.tamanho ? formatarTamanho(doc.tamanho) : 'N/A'} • 
                          {doc.uploadedBy ? ` Enviado por ${doc.uploadedBy}` : ''}
                        </p>
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Abrir
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Nenhum documento anexado a esta solicitação.</p>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Alerta Interativo */}
      <AlertModal
        isOpen={alertModal.open}
        onClose={() => setAlertModal(prev => ({ ...prev, open: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        messages={alertModal.messages}
        confirmText={alertModal.confirmText || 'Entendi'}
        onConfirm={alertModal.onConfirm}
        autoClose={alertModal.autoClose}
      />
    </div>
  );
};

export default Inicio;
